import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, ChevronRight, ChevronDown } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VoiceProfilesTab() {
    const { getAuthHeader } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [questions, setQuestions] = useState({});
    const [expandedCat, setExpandedCat] = useState(null);

    useEffect(() => { loadUsers(); }, []);

    const loadUsers = async () => {
        try {
            const [usersRes, qRes] = await Promise.all([
                axios.get(`${API}/admin/voice-profiles`, { headers: getAuthHeader() }),
                axios.get(`${API}/voice-profile/questions`, { headers: getAuthHeader() }),
            ]);
            setUsers(usersRes.data);
            setQuestions(qRes.data);
        } catch { toast.error('Failed to load voice profiles'); }
        finally { setLoading(false); }
    };

    const viewProfile = async (userId) => {
        try {
            const res = await axios.get(`${API}/admin/voice-profiles/${userId}`, { headers: getAuthHeader() });
            setProfile(res.data);
            setSelectedUser(users.find(u => u.user_id === userId));
            setExpandedCat(null);
        } catch { toast.error('Failed to load profile'); }
    };

    if (loading) return <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    if (selectedUser && profile) {
        const answers = profile.answers || {};
        return (
            <div data-testid="voice-profile-detail">
                <button onClick={() => { setSelectedUser(null); setProfile(null); }} className="flex items-center gap-1 text-sm text-stone-700 hover:text-stone-900 mb-4" data-testid="back-to-profiles">
                    <ChevronRight className="w-4 h-4 rotate-180" />Back to all profiles
                </button>
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 font-bold">{selectedUser.name?.charAt(0) || '?'}</div>
                    <div>
                        <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedUser.name}</h3>
                        <p className="text-xs text-stone-500">{selectedUser.email} &middot; {profile.completion_pct || 0}% complete &middot; {profile.answered_count || 0} answers</p>
                    </div>
                </div>
                {Object.keys(answers).length === 0 ? (
                    <div className="text-center py-10 text-stone-400">
                        <Mic className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p>This user has not started their voice profile yet.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {Object.entries(questions).map(([catKey, cat]) => {
                            const catAnswers = answers[catKey] || {};
                            const hasAnswers = Object.values(catAnswers).some(v => v?.trim());
                            if (!hasAnswers) return null;
                            const isExpanded = expandedCat === catKey;
                            return (
                                <div key={catKey} className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`admin-cat-${catKey}`}>
                                    <button onClick={() => setExpandedCat(isExpanded ? null : catKey)}
                                        className="w-full flex items-center justify-between p-3 hover:bg-stone-50 text-left">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-sm font-semibold text-stone-700">{cat.title}</h4>
                                            <span className="text-xs text-stone-400">{Object.values(catAnswers).filter(v => v?.trim()).length}/{cat.questions.length}</span>
                                        </div>
                                        {isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                                    </button>
                                    {isExpanded && (
                                        <div className="border-t border-stone-100 divide-y divide-stone-100">
                                            {cat.questions.map((q, idx) => {
                                                const answer = catAnswers[String(idx)] || '';
                                                if (!answer.trim()) return null;
                                                return (
                                                    <div key={idx} className="p-3" data-testid={`admin-answer-${catKey}-${idx}`}>
                                                        <p className="text-xs font-medium text-stone-500 mb-1">{q}</p>
                                                        <p className="text-sm text-stone-800 whitespace-pre-wrap">{answer}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div data-testid="voice-profiles-list">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Voice of the User Profiles</h3>
            </div>
            <div className="space-y-2">
                {users.map(u => (
                    <div key={u.user_id} className="flex items-center gap-4 p-3 rounded-lg border border-stone-200 hover:border-stone-300 hover:bg-stone-100/20 transition-colors cursor-pointer"
                        onClick={() => viewProfile(u.user_id)} data-testid={`user-profile-${u.user_id}`}>
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 font-bold text-sm flex-shrink-0">{u.name?.charAt(0) || '?'}</div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-stone-800">{u.name}</p>
                                {u.is_admin && <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-700 rounded-full">Admin</span>}
                            </div>
                            <p className="text-xs text-stone-400">{u.email}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            {u.has_profile ? (
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-24 bg-stone-100 rounded-full h-2 overflow-hidden">
                                            <div className={`h-full rounded-full ${u.completion_pct >= 80 ? 'bg-emerald-500' : u.completion_pct >= 40 ? 'bg-amber-500' : 'bg-stone-1000'}`} style={{ width: `${u.completion_pct}%` }} />
                                        </div>
                                        <span className="text-xs font-medium text-stone-600 w-10 text-right">{u.completion_pct}%</span>
                                    </div>
                                    <p className="text-xs text-stone-400">{u.answered_count} answers</p>
                                </div>
                            ) : (
                                <span className="text-xs text-stone-400">Not started</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AdminVoiceProfilesPage() {
    return (
        <AdminLayout title="Voice Profiles" description="View voice profile responses from team members">
            <VoiceProfilesTab />
        </AdminLayout>
    );
}
